/* Admin Chatbot Training & Analytics. Three tabs:
   1. Knowledge — FAQs, blocked topics, disclaimer, page starters, quick replies, key links
   2. Sessions — recent chat sessions with intent class + tags
   3. Analytics — stats rollup
   Scott can update everything here without a redeploy. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const PAGE_OPTIONS = [
  ["/", "Homepage"], ["/inventory", "Inventory list"], ["/value-my-trade", "Trade page"],
  ["/scotts-deal-desk", "Finance / payment page"], ["/electric-vehicles", "EV page"],
  ["/about-scott", "About Scott"], ["/schedule-test-drive", "Schedule test drive"],
  ["/contact", "Contact"], ["/chat", "Standalone chat page"],
];

export default function AdminChatbot() {
  const [tab, setTab] = useState("knowledge");
  return (
    <div className="bg-slate-50 min-h-full" data-testid="page-chatbot-admin">
      <div className="bg-gradient-to-b from-[#061426] to-[#0a1f3a] text-white border-b border-white/10">
        <div className="px-5 py-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300 font-bold">Growth</div>
          <h1 className="text-2xl sm:text-3xl font-extrabold mt-0.5">Chatbot Training & Analytics</h1>
          <p className="text-sm text-blue-200/70 mt-1">Update Scott's virtual assistant in real time — no redeploy needed.</p>
          <div className="mt-3 flex gap-2">
            <Link to="/chat" target="_blank" className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-emerald-500/30 text-emerald-200 hover:bg-emerald-500/40" data-testid="chatbot-open-public">Open public chat ↗</Link>
            <Link to="/admin/today" className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-white/10 text-white/80 hover:bg-white/20">← Dashboard</Link>
          </div>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex flex-wrap gap-1 border-b border-gray-200">
          {[{ k: "knowledge", l: "Knowledge" }, { k: "sessions", l: "Sessions" }, { k: "analytics", l: "Analytics" }].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`text-sm px-4 py-2 border-b-2 ${tab === t.k ? "border-[#006DFF] text-[#006DFF] font-semibold" : "border-transparent text-gray-600 hover:text-[#061426]"}`}
              data-testid={`chatbot-tab-${t.k}`}>
              {t.l}
            </button>
          ))}
        </div>
        {tab === "knowledge" && <KnowledgeTab />}
        {tab === "sessions" && <SessionsTab />}
        {tab === "analytics" && <AnalyticsTab />}
      </div>
    </div>
  );
}

/* =========================================================================
   KNOWLEDGE TAB — FAQs, blocked topics, disclaimer, page starters, links
   ========================================================================= */
function KnowledgeTab() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/chatbot/config");
      setData(r.data); setDraft(JSON.parse(JSON.stringify(r.data.config)));
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true); setError(""); setSaved(false);
    try {
      // Only send the editable subset (server merges with defaults)
      const body = {
        scott_phone: draft.scott_phone,
        contact_url: draft.contact_url, inventory_url: draft.inventory_url,
        trade_url: draft.trade_url, finance_url: draft.finance_url,
        test_drive_url: draft.test_drive_url, ev_url: draft.ev_url, about_url: draft.about_url,
        page_starters: draft.page_starters,
        quick_replies: draft.quick_replies,
        faqs: draft.faqs,
        blocked_topics: draft.blocked_topics,
        disclaimer: draft.disclaimer,
        after_hours_response: draft.after_hours_response,
        lead_notify_email: draft.lead_notify_email,
      };
      await api.patch("/admin/chatbot/config", body);
      setSaved(true);
      await load();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const resetStarters = async () => {
    if (!window.confirm("Reset all page starters to defaults?")) return;
    setSaving(true);
    try {
      await api.post("/admin/chatbot/config/reset-page-starters");
      await load();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  if (!draft) return <p className="text-gray-500 text-sm">Loading…</p>;

  const inputCls = "w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500";
  const ta = (k, rows = 2) => (
    <textarea value={draft[k] || ""} onChange={e => setDraft({ ...draft, [k]: e.target.value })} rows={rows}
      className={inputCls} data-testid={`chatbot-${k}`} />
  );
  const txt = (k) => (
    <input value={draft[k] || ""} onChange={e => setDraft({ ...draft, [k]: e.target.value })}
      className={inputCls} data-testid={`chatbot-${k}`} />
  );

  return (
    <div className="space-y-4" data-testid="knowledge-tab">
      {error && <div className="bg-red-50 border border-red-300 text-red-800 text-sm rounded p-3" data-testid="chatbot-error">{error}</div>}
      {saved && <div className="bg-emerald-50 border border-emerald-300 text-emerald-900 text-sm rounded p-3" data-testid="chatbot-saved">✓ Saved. Live on the next chat turn.</div>}

      {/* Key links + phone */}
      <Section title="Links & contact" subtitle="Used everywhere the bot points customers to a page.">
        <Grid>
          <Field label="Scott's phone">{txt("scott_phone")}</Field>
          <Field label="Lead notify email (optional CC)">{txt("lead_notify_email")}</Field>
          <Field label="Inventory page URL">{txt("inventory_url")}</Field>
          <Field label="Trade page URL">{txt("trade_url")}</Field>
          <Field label="Finance / payment URL">{txt("finance_url")}</Field>
          <Field label="Test drive URL">{txt("test_drive_url")}</Field>
          <Field label="EV page URL">{txt("ev_url")}</Field>
          <Field label="About Scott URL">{txt("about_url")}</Field>
          <Field label="Contact URL">{txt("contact_url")}</Field>
        </Grid>
      </Section>

      {/* Disclaimer / after-hours */}
      <Section title="Approved messaging" subtitle="Customer-facing copy. Keep these short.">
        <Field label="Disclaimer banner (shown above every chat)">{ta("disclaimer", 3)}</Field>
        <Field label="After-hours response">{ta("after_hours_response", 2)}</Field>
      </Section>

      {/* Page starters */}
      <Section title="Per-page opening messages"
               subtitle="The greeting the bot shows when a visitor opens chat on each page."
               actions={<button onClick={resetStarters} className="text-xs text-blue-700 hover:text-blue-900 font-semibold" data-testid="chatbot-reset-starters">Reset to defaults</button>}>
        <div className="space-y-2">
          {PAGE_OPTIONS.map(([path, label]) => (
            <div key={path} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start">
              <div className="text-xs">
                <div className="font-bold text-[#061426]">{label}</div>
                <code className="text-[10px] text-gray-500">{path}</code>
              </div>
              <textarea
                value={(draft.page_starters || {})[path] || ""}
                onChange={e => setDraft({ ...draft, page_starters: { ...(draft.page_starters || {}), [path]: e.target.value } })}
                rows={2}
                className="md:col-span-3 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                data-testid={`chatbot-starter-${path.replace(/[^a-z]/g, "-")}`}
              />
            </div>
          ))}
        </div>
      </Section>

      {/* Quick replies */}
      <Section title="Quick-reply buttons" subtitle="The chips shown under the first message. Max 7 recommended.">
        <div className="space-y-2">
          {(draft.quick_replies || []).map((qr, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
              <input value={qr.key} onChange={e => updateAt(setDraft, draft, "quick_replies", i, { ...qr, key: e.target.value })} placeholder="key" className="md:col-span-2 border border-gray-300 rounded px-2 py-1.5 text-xs font-mono" />
              <input value={qr.label} onChange={e => updateAt(setDraft, draft, "quick_replies", i, { ...qr, label: e.target.value })} placeholder="button label" className="md:col-span-3 border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input value={qr.starter || ""} onChange={e => updateAt(setDraft, draft, "quick_replies", i, { ...qr, starter: e.target.value })} placeholder="message sent when tapped" className="md:col-span-6 border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <button onClick={() => setDraft({ ...draft, quick_replies: draft.quick_replies.filter((_, j) => j !== i) })} className="md:col-span-1 text-xs text-red-600 hover:underline">Remove</button>
            </div>
          ))}
          <button onClick={() => setDraft({ ...draft, quick_replies: [...(draft.quick_replies || []), { key: `qr_${Date.now()}`, label: "New button", starter: "" }] })}
            className="text-xs font-bold text-blue-700 hover:text-blue-900" data-testid="chatbot-add-quickreply">+ Add quick-reply button</button>
        </div>
      </Section>

      {/* FAQs */}
      <Section title="FAQs injected into the bot's brain" subtitle="The bot sees these as approved answers it can use verbatim.">
        <div className="space-y-3">
          {(draft.faqs || []).map((f, i) => (
            <div key={i} className="bg-slate-50 border border-gray-200 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">FAQ #{i + 1}</div>
                <button onClick={() => setDraft({ ...draft, faqs: draft.faqs.filter((_, j) => j !== i) })} className="text-xs text-red-600 hover:underline">Remove</button>
              </div>
              <input value={f.q} onChange={e => updateAt(setDraft, draft, "faqs", i, { ...f, q: e.target.value })} placeholder="Question" className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-semibold" />
              <textarea value={f.a} onChange={e => updateAt(setDraft, draft, "faqs", i, { ...f, a: e.target.value })} placeholder="Approved answer" rows={2} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
          ))}
          <button onClick={() => setDraft({ ...draft, faqs: [...(draft.faqs || []), { q: "", a: "" }] })}
            className="text-xs font-bold text-blue-700 hover:text-blue-900" data-testid="chatbot-add-faq">+ Add FAQ</button>
        </div>
      </Section>

      {/* Blocked topics */}
      <Section title="Blocked topics" subtitle="The bot politely redirects when asked about any of these.">
        <input
          value={(draft.blocked_topics || []).join(", ")}
          onChange={e => setDraft({ ...draft, blocked_topics: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
          placeholder="politics, religion, competitor_pricing_negotiation, ..."
          className={inputCls + " font-mono text-xs"}
          data-testid="chatbot-blocked-topics"
        />
      </Section>

      {/* Save bar */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 -mx-5 px-5 py-3 flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {data?.config?.updated_at ? `Last saved ${data.config.updated_at.slice(0, 16).replace("T", " ")} by ${data.config.updated_by || "—"}` : "Never saved."}
        </div>
        <button onClick={save} disabled={saving} className="bg-[#061426] hover:bg-[#0a1f3a] disabled:opacity-50 text-white text-xs font-bold px-5 py-2 rounded" data-testid="chatbot-save">{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}

function updateAt(setDraft, draft, key, idx, value) {
  const next = [...(draft[key] || [])];
  next[idx] = value;
  setDraft({ ...draft, [key]: next });
}

/* =========================================================================
   SESSIONS TAB
   ========================================================================= */
function SessionsTab() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [intent, setIntent] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const params = {};
      if (intent) params.intent_class = intent;
      const r = await api.get("/admin/chatbot/sessions", { params });
      setItems(r.data.items || []);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [intent]);

  const openSession = async (sid) => {
    try {
      const r = await api.get(`/admin/chatbot/sessions/${encodeURIComponent(sid)}`);
      setActive(sid); setTranscript(r.data);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-3" data-testid="sessions-tab">
      {error && <div className="bg-red-50 border border-red-300 text-red-800 text-sm rounded p-3">{error}</div>}
      <div className="bg-white border border-gray-200 rounded p-3 flex flex-wrap gap-2 items-center">
        <div className="flex gap-1">
          {[
            { k: "", l: "All" },
            { k: "high_intent_lead", l: "Hot leads" },
            { k: "serious_shopper", l: "Serious" },
            { k: "casual_browser", l: "Browsers" },
            { k: "playful_tester", l: "Playful" },
            { k: "likely_troll", l: "Trolls" },
          ].map(t => (
            <button key={t.k} onClick={() => setIntent(t.k)}
              className={`text-xs px-3 py-1.5 rounded font-semibold ${intent === t.k ? "bg-[#061426] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
              data-testid={`session-filter-${t.k || "all"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <button onClick={load} className="text-xs font-semibold text-blue-700 hover:text-blue-900 ml-auto">↻ Refresh</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
        <div className="lg:col-span-2 bg-white rounded border border-gray-200 overflow-hidden">
          <table className="w-full text-sm" data-testid="session-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-gray-500">
              <tr><th className="text-left px-3 py-2">Session</th><th className="text-left px-3 py-2">Intent</th><th className="text-right px-3 py-2">Turns</th></tr>
            </thead>
            <tbody>
              {items.map(s => (
                <tr key={s.session_id} onClick={() => openSession(s.session_id)} className={`border-t border-gray-100 cursor-pointer hover:bg-blue-50 ${active === s.session_id ? "bg-blue-100" : ""}`} data-testid={`session-row-${s.session_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono text-[10px] text-gray-500">{s.session_id?.slice(0, 16)}</div>
                    <div className="text-[10px] text-gray-400">{(s.last_turn_at || "").slice(0, 16).replace("T", " ")}</div>
                  </td>
                  <td className="px-3 py-2"><IntentPill v={s.last_intent_class} /></td>
                  <td className="px-3 py-2 text-right text-xs font-bold">{s.turns || 0}</td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-gray-400">No sessions yet.</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-3 bg-white rounded border border-gray-200 p-3">
          {!transcript ? (
            <p className="text-sm text-gray-400 text-center py-12">Select a session on the left to see the transcript.</p>
          ) : (
            <div className="space-y-3" data-testid="session-transcript">
              <div className="border-b border-gray-200 pb-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Session</div>
                <div className="font-mono text-xs">{transcript.session?.session_id}</div>
                <div className="flex flex-wrap gap-1 mt-1">
                  <IntentPill v={transcript.session?.last_intent_class} />
                  {(transcript.session?.tags || []).map(t => <Tag key={t}>{t}</Tag>)}
                </div>
              </div>
              <div className="max-h-[480px] overflow-y-auto space-y-2">
                {(transcript.messages || []).map(m => (
                  <div key={m.id} className="text-sm border-b border-gray-100 pb-2">
                    <div className="text-[10px] text-gray-500">{(m.created_at || "").slice(0, 16).replace("T", " ")} · {m.page_path || "—"}</div>
                    <div className="bg-blue-50 rounded px-2 py-1 mt-1 text-gray-800"><b>You:</b> {m.user}</div>
                    <div className="bg-slate-50 rounded px-2 py-1 mt-1 text-gray-700"><b>Bot:</b> {m.assistant}</div>
                    {(m.tags || []).length > 0 && <div className="flex flex-wrap gap-1 mt-1">{m.tags.map(t => <Tag key={t}>{t}</Tag>)}</div>}
                  </div>
                ))}
                {(transcript.messages || []).length === 0 && <p className="text-gray-400 text-sm">No messages logged on this session.</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   ANALYTICS TAB
   ========================================================================= */
function AnalyticsTab() {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const load = async () => {
    try { const r = await api.get(`/admin/chatbot/stats?days=${days}`); setStats(r.data); }
    catch (e) { setError(e?.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (error) return <div className="bg-red-50 border border-red-300 text-red-800 text-sm rounded p-3">{error}</div>;
  if (!stats) return <p className="text-gray-500 text-sm">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      <div className="flex gap-2 items-center">
        {[7, 30, 90].map(d => (
          <button key={d} onClick={() => setDays(d)}
            className={`text-xs px-3 py-1.5 rounded font-semibold ${days === d ? "bg-[#061426] text-white" : "bg-gray-100 text-gray-700"}`}
            data-testid={`analytics-range-${d}`}>
            Last {d} days
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="analytics-kpis">
        <Stat label="Sessions" value={stats.total_sessions} />
        <Stat label="Messages" value={stats.total_messages} />
        <Stat label="Chatbot leads" value={stats.chatbot_leads} accent="blue" />
        <Stat label="Hot leads" value={stats.hot_leads} accent={stats.hot_leads ? "red" : null} />
        <Stat label="Phones captured" value={stats.phones_captured} accent="emerald" />
        <Stat label="Browsers" value={stats.by_intent_class?.casual_browser || 0} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card title="By intent class">
          {Object.entries(stats.by_intent_class || {}).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
            <Row key={k} label={<IntentPill v={k} />} value={n} />
          ))}
          {Object.keys(stats.by_intent_class || {}).length === 0 && <p className="text-xs text-gray-400">No sessions in this range.</p>}
        </Card>
        <Card title="Top pages">
          {Object.entries(stats.top_pages || {}).slice(0, 10).map(([k, n]) => (
            <Row key={k} label={<code className="text-[11px]">{k}</code>} value={n} />
          ))}
          {Object.keys(stats.top_pages || {}).length === 0 && <p className="text-xs text-gray-400">No data.</p>}
        </Card>
      </div>

      <Card title="Tag rollup (last 30 days)">
        <div className="flex flex-wrap gap-2">
          {Object.entries(stats.tag_counts || {}).map(([k, n]) => (
            <span key={k} className="bg-slate-100 border border-gray-200 rounded-full px-2 py-1 text-xs">
              <b>{k}</b> · {n}
            </span>
          ))}
          {Object.keys(stats.tag_counts || {}).length === 0 && <p className="text-xs text-gray-400">No tagged conversations yet.</p>}
        </div>
      </Card>
    </div>
  );
}

/* ----- shared small UI helpers ----- */
function Section({ title, subtitle, actions, children }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-bold text-[#061426]">{title}</h3>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {actions}
      </div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>; }
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
function IntentPill({ v }) {
  if (!v) return <span className="text-xs text-gray-400">—</span>;
  const map = {
    high_intent_lead: ["🔥 Hot", "bg-red-100 text-red-700"],
    serious_shopper:  ["📍 Serious", "bg-emerald-100 text-emerald-700"],
    casual_browser:   ["Browser", "bg-blue-100 text-blue-700"],
    playful_tester:   ["😄 Playful", "bg-amber-100 text-amber-700"],
    likely_troll:     ["🚧 Troll", "bg-gray-200 text-gray-700"],
    unrelated_or_unknown: ["? Unknown", "bg-gray-100 text-gray-500"],
  };
  const [label, cls] = map[v] || [v, "bg-gray-100 text-gray-700"];
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${cls}`}>{label}</span>;
}
function Tag({ children }) { return <span className="text-[10px] bg-slate-100 border border-gray-200 text-gray-700 rounded px-1.5 py-0.5">{children}</span>; }
function Stat({ label, value, accent }) {
  const c = accent === "emerald" ? "text-emerald-600" : accent === "blue" ? "text-blue-600" : accent === "red" ? "text-red-600" : "text-[#061426]";
  return (
    <div className="bg-white border border-gray-200 rounded p-2 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-2xl font-bold ${c}`}>{value ?? 0}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
      <h4 className="text-xs uppercase tracking-wider font-bold text-[#061426] mb-2">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-gray-100 last:border-0">
      <div>{label}</div><div className="font-bold">{value}</div>
    </div>
  );
}
